import { apiClient } from '@/services/Axios';

/**
 * Unwrap a v1 `{ data, meta }` response.
 * @param {import('axios').AxiosResponse} response
 */
export function unwrapV1(response) {
  const payload = response?.data ?? {};
  return {
    data: payload.data,
    meta: payload.meta ?? {},
  };
}

/**
 * Map v1 pagination meta to the legacy list shape used across the app.
 * @param {unknown[]} data
 * @param {Record<string, unknown>} meta
 */
export function toLegacyPaginated(data, meta = {}) {
  const page = meta.page ?? meta.current_page ?? 1;
  const perPage = meta.pageSize ?? meta.per_page ?? (Array.isArray(data) ? data.length : 0);
  const total = meta.total ?? (Array.isArray(data) ? data.length : 0);
  const lastPage = meta.lastPage ?? meta.last_page ?? 1;

  return {
    success: true,
    data: Array.isArray(data) ? data : [],
    current_page: page,
    per_page: perPage,
    total,
    last_page: lastPage,
    pagination: {
      current_page: page,
      per_page: perPage,
      total,
      last_page: lastPage,
    },
  };
}

/**
 * Map v1 user option rows (`id`, `label`, `secondaryLabel`) to legacy chat/assignee shapes.
 * @param {unknown} data
 */
export function mapUserOptionRows(data) {
  return (Array.isArray(data) ? data : []).map((row) => ({
    id: row.id,
    name: row.label ?? row.name ?? row.display_name ?? row.email,
    display_name: row.label ?? row.display_name ?? row.name,
    email: row.secondaryLabel ?? row.email,
  }));
}

/**
 * Map v1 detail payload to legacy `{ success, data }`.
 * @param {unknown} data
 */
export function toLegacyDetail(data) {
  return {
    success: true,
    data,
  };
}

/**
 * GET a v1 endpoint and return `{ data, meta }`.
 * @param {string} path
 * @param {Record<string, unknown>} [params]
 */
export async function v1Get(path, params = {}) {
  const response = await apiClient.get(path, { params });
  return unwrapV1(response);
}

/**
 * POST a v1 endpoint and return `{ data, meta }`.
 * @param {string} path
 * @param {Record<string, unknown>} [body]
 */
export async function v1Post(path, body = {}) {
  const response = await apiClient.post(path, body);
  return unwrapV1(response);
}

export async function v1Patch(path, body = {}) {
  const response = await apiClient.patch(path, body);
  return unwrapV1(response);
}

export async function v1Put(path, body = {}) {
  const response = await apiClient.put(path, body);
  return unwrapV1(response);
}

export async function v1Delete(path) {
  const response = await apiClient.delete(path);
  if (response.status === 204 || response.data == null || response.data === '') {
    return { data: null, meta: {} };
  }
  return unwrapV1(response);
}

/**
 * Try v1 first; fall back to legacy on failure.
 * @param {() => Promise<unknown>} v1Fn
 * @param {() => Promise<unknown>} legacyFn
 */
export async function withLegacyFallback(v1Fn, legacyFn) {
  try {
    return await v1Fn();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403 || status === 422) {
      throw error;
    }
    return legacyFn();
  }
}

/**
 * Map v1 equipment list show to legacy `{ success, data, totals }`.
 * @param {Record<string, unknown>} data
 * @param {Record<string, unknown>} meta
 */
export function toLegacyEquipmentListContent(data, meta = {}) {
  return {
    success: true,
    data,
    totals: meta.totals ?? null,
  };
}
/**
 * Build v1 query params from legacy snake_case filters.
 * @param {Record<string, unknown>} filters
 */
export function mapLocalAssetFiltersToV1(filters = {}) {
  const params = {};

  if (filters.page != null) params.page = filters.page;
  if (filters.per_page != null) params.pageSize = filters.per_page;
  if (filters.search) params.search = filters.search;
  if (filters.type) params.type = filters.type;
  if (filters.department_id != null) params.departmentId = filters.department_id;
  if (filters.master_category_id != null) params.masterCategoryId = filters.master_category_id;
  if (filters.category_id != null) params.categoryId = filters.category_id;
  if (filters.exclude_asset_id != null) params.excludeAssetId = filters.exclude_asset_id;
  if (filters.exclude_id != null) params.excludeAssetId = filters.exclude_id;
  if (filters.is_active != null) params.isActive = filters.is_active;
  if (filters.sort_by) params.sortBy = filters.sort_by;
  if (filters.sort_order) params.sortOrder = filters.sort_order;

  return params;
}

/**
 * Legacy local-assets list responses omit `success`; keep that shape for callers.
 * @param {unknown[]} data
 * @param {Record<string, unknown>} meta
 */
export function toLegacyLocalAssetsList(data, meta = {}) {
  const paginated = toLegacyPaginated(data, meta);
  return {
    data: paginated.data,
    current_page: paginated.current_page,
    per_page: paginated.per_page,
    total: paginated.total,
    last_page: paginated.last_page,
  };
}
